/**
 * Componentes visuais compartilhados por mais de um módulo.
 *
 * Tudo que aparece em telas de módulos diferentes mora aqui. Componentes usados
 * por várias páginas de um mesmo módulo ficam em `./modules/<modulo>.tsx`, e o que
 * é exclusivo de uma página continua declarado na própria view.
 */
import { keyframes, styled } from '@mui/material/styles'

import FilterListIcon from '@mui/icons-material/FilterList'

import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Slider from '@mui/material/Slider'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import { cardSx } from './lifeshub-tokens'

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Barra superior (toolbar escura)
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

/** Separador vertical da toolbar nas páginas já migradas para o design LifesHub. */
export const ToolbarDivider = styled(Divider)({
    borderRightWidth: 'medium',
    borderColor: 'rgba(255, 255, 255, .13)',
    marginRight: 5
})

/** Separador vertical da toolbar nas páginas ainda não migradas (perfil, RD Station, main). */
export const ToolbarDividerLegacy = styled(Divider)({
    borderRightWidth: 'medium',
    borderColor: '#333333a6',
    marginRight: 5
})

/** Título da seção na toolbar. */
export const ToolbarSectionTitle = styled('div')({
    minWidth: 0,
    color: 'var(--green)',
    fontSize: 17,
    fontWeight: 600,
    letterSpacing: '.01em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
})

/** Variante do título da toolbar usada onde ela divide espaço com a busca. */
export const ToolbarSectionTitleResponsive = styled('div')({
    minWidth: 100,
    color: 'var(--green)',
    fontSize: 17,
    fontWeight: 600,
    letterSpacing: '.01em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '@media (max-width: 900px)': { fontSize: 15 }
})

/** Switch com rótulo sobre o fundo escuro da toolbar. */
export const ToolbarToggleRow = styled(FormControlLabel)({
    margin: 0,
    gap: 2,
    '& .MuiFormControlLabel-label': {
        fontSize: 11.5,
        fontWeight: 700,
        color: 'rgba(255, 255, 255, .78)',
        whiteSpace: 'nowrap'
    },
    '& .MuiSwitch-track': { backgroundColor: 'rgba(255, 255, 255, .3)', opacity: 1 },
    '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: 'var(--green-700)',
        opacity: 1
    }
})

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Estrutura de página
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

/** Raiz da view de um módulo. */
export const ModuleView = styled('div')({
    display: 'flex',
    flexGrow: 1,
    minWidth: 0,
    color: 'var(--ink)',
    fontSize: 14,
    lineHeight: 1.45
})

/** Área rolável de uma página com cards empilhados. */
export const ScrollablePageView = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '26px 28px 42px',
    overflowX: 'hidden',
    overflowY: 'auto',
    flex: '1 1 0',
    minWidth: 0,
    '@media (max-width: 900px)': { padding: '22px 18px 36px' }
})

/** Cabeçalho da página (título à esquerda, ações à direita). */
export const PageHead = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    marginBottom: 2,
    flexShrink: 0,
    '@media (max-width: 720px)': { flexDirection: 'column', gap: 12 }
})

/** Variante do cabeçalho para páginas montadas sobre grid (`gridArea: 'head'`). */
export const PageHeadGrid = styled('div')({
    gridArea: 'head',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    marginBottom: 4,
    '@media (max-width: 720px)': {
        flexDirection: 'column',
        '& > div:last-of-type': { width: '100%' }
    }
})

export const PageTitle = styled('h1')({
    margin: '0 0 6px',
    fontSize: 'clamp(26px, 2.1vw, 36px)',
    lineHeight: 1.15,
    letterSpacing: '-.025em',
    color: 'var(--ink)'
})

export const PageSubtitle = styled('p')({
    margin: 0,
    maxWidth: 920,
    color: 'var(--muted)'
})

export const Breadcrumbs = styled('div')({
    color: 'var(--muted)',
    fontSize: 12,
    marginBottom: 7,
    '& b': { color: 'var(--green-800)', fontWeight: 650 }
})

/** Linha de botões alinhada à direita. */
export const ActionsRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    flexWrap: 'wrap'
})

/** Linha de botões que passam a ocupar a largura toda no mobile. */
export const ActionsRowResponsive = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    flexWrap: 'wrap',
    '@media (max-width: 720px)': {
        '& .MuiButton-root': { flexGrow: 1 }
    }
})

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Página de edição (cabeçalho fixo + duas colunas)
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

export const EditorView = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    padding: '0 28px 42px',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    '@media (max-width: 900px)': { padding: '0 18px 36px' }
})

/** Faixa fixa no topo do editor com o contexto (breadcrumb, título e ações). */
export const EditorContextBar = styled('div')({
    position: 'sticky',
    top: 0,
    zIndex: 30,
    margin: '0 -28px 22px',
    padding: '26px 28px 0',
    backgroundColor: 'rgba(255, 255, 255, .96)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid var(--border)',
    boxShadow: '0 4px 18px rgba(0,0,0,.025)',
    '@media (max-width: 900px)': { margin: '0 -18px 22px', padding: '22px 18px 0' },
    '@media (max-width: 720px)': { position: 'static' }
})

/** Corpo do editor em duas colunas (conteúdo + coluna lateral). */
export const EditorBody = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2.05fr) minmax(300px, .9fr)',
    gap: 11,
    alignItems: 'start',
    '@media (max-width: 1050px)': { gridTemplateColumns: 'minmax(0, 1fr)' }
})

export const EditorSideColumn = styled('aside')({
    display: 'grid',
    gap: 10,
    minWidth: 0,
    position: 'sticky',
    top: 210,
    '@media (max-width: 1050px)': { position: 'static' }
})

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Cards e painéis
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

/** Card de formulário/painel (não encolhe na horizontal). */
export const PanelCard = styled(Paper)({
    ...cardSx,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden'
})

export const PanelCardBody = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minHeight: 0,
    flexGrow: 1,
    padding: '0 18px 18px'
})

/** Área do gráfico dentro de um card. */
export const ChartArea = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    padding: '0 18px 18px'
})

export const FlexPaper = styled(Paper)({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    paddingLeft: 3,
    paddingRight: 3
})

/** Ícone circular usado nos estados vazios. */
export const EmptyIcon = styled('div')({
    width: 52,
    height: 52,
    display: 'grid',
    placeItems: 'center',
    marginBottom: 8,
    borderRadius: '50%',
    color: 'var(--green-800)',
    backgroundColor: 'var(--green-100)',
    '& .MuiSvgIcon-root': { fontSize: 24 }
})

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Pares chave/valor
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

export const KeyValues = styled('div')({
    display: 'grid',
    gap: 13
})

export const KeyRow = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'minmax(115px, .8fr) minmax(0, 1fr)',
    gap: 16,
    alignItems: 'start',
    '& .key': { color: 'var(--muted)', fontSize: 12 },
    '& .value': { fontWeight: 650, minWidth: 0, overflowWrap: 'anywhere' },
    '@media (max-width: 720px)': { gridTemplateColumns: 'minmax(0, 1fr)', gap: 4 }
})

export const KeyDivider = styled('div')({
    height: 1,
    backgroundColor: 'var(--border)',
    margin: '8px 0'
})

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Campos de formulário
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

export const Field = styled('div')({
    display: 'grid',
    gap: 6,
    minWidth: 0
})

export const FieldLabel = styled('label')({
    fontSize: 12,
    fontWeight: 700,
    color: '#303733'
})

export const FieldHelp = styled('div')({
    fontSize: 11.5,
    lineHeight: 1.35,
    color: 'var(--muted)'
})

/** Switch com título e descrição ao lado. */
export const SwitchRow = styled('div')({
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    minWidth: 0,
    '& b': { display: 'block', fontSize: 13 }
})

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Botões de ícone
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

/** Botão de ícone padrão sobre fundo claro (colapsar painel, abrir menu, expandir). */
export const IconActionButton = styled(IconButton)({
    width: 34,
    height: 34,
    borderRadius: 8,
    color: '#2d332f',
    '&:hover': { backgroundColor: '#edf1ee' }
})

/** Botão de ícone do cabeçalho de um card (ícone um pouco menor). */
export const CardIconButton = styled(IconButton)({
    width: 34,
    height: 34,
    borderRadius: 8,
    color: '#2d332f',
    '&:hover': { backgroundColor: '#edf1ee' },
    '& .MuiSvgIcon-root': { fontSize: 20 }
})

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Painel de filtros
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

export const FilterPaneContainer = styled(Card)({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    alignSelf: 'stretch',
    flexShrink: 0,
    backgroundColor: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-1)',
    backgroundImage: 'none',
    overflow: 'hidden',
    paddingBottom: 112
})

export const FilterPaneHeader = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'stretch',
    alignItems: 'center',
    gap: 8,
    padding: '14px 10px 14px 16px',
    borderBottom: '1px solid var(--border)',
    boxSizing: 'border-box',
    flexShrink: 0,
    '& .MuiSvgIcon-root': { fontSize: 20 }
})

export const PaneTitle = styled('h3')({
    margin: 0,
    flexGrow: 1,
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--ink)',
    lineHeight: 1.2
})

/** Cabeçalho do painel de filtros quando ele está colapsado (texto na vertical). */
export const CollapsedPaneHeader = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    padding: '10px 0',
    color: 'var(--muted)'
})

export const CollapsedPaneLabel = styled('span')({
    writingMode: 'vertical-rl',
    textOrientation: 'mixed',
    marginTop: 5,
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--muted)'
})

export const CollapsedPaneFilterIcon = styled(FilterListIcon)({
    transform: 'rotate(90deg)',
    fontSize: 20
})

/** Faixa fixa no rodapé do painel de filtros com o botão de aplicar. */
export const ApplyBand = styled('div')({
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '11px 14px',
    backgroundColor: 'rgba(255, 255, 255, .98)',
    borderTop: '1px solid var(--border)',
    '& .MuiButton-root': { width: '100%' }
})

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Diálogos
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

export const ModalContent = styled('div')({
    padding: '24px 26px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14
})

export const ModalHead = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    gap: 18,
    alignItems: 'flex-start',
    marginBottom: 4,
    '& h2': { margin: '0 0 5px', fontSize: 20, lineHeight: 1.2, color: 'var(--ink)' },
    '& p': { margin: 0, color: 'var(--muted)', fontSize: 13 }
})

export const ModalClose = styled(IconButton)({
    width: 36,
    height: 36,
    borderRadius: 8,
    color: '#2d332f',
    '&:hover': { backgroundColor: '#f0f3f1' }
})

export const ModalFooter = styled('div')({
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: '14px 26px',
    backgroundColor: 'rgba(255, 255, 255, .98)',
    borderTop: '1px solid var(--border)',
    '@media (max-width: 720px)': {
        flexDirection: 'column',
        alignItems: 'stretch'
    }
})

export const ModalFooterActions = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginLeft: 'auto',
    '@media (max-width: 720px)': {
        '& > *': { flexGrow: 1 }
    }
})

export const FooterNote = styled('div')({
    color: 'var(--muted)',
    fontSize: 12,
    minWidth: 0
})

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Abas de listagem
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

export const ListagemTabs = styled(Tabs)({
    minHeight: 42,
    flexGrow: 1,
    '& .MuiTabs-flexContainer': { gap: 8 },
    '& .MuiTabs-indicator': {
        height: 3,
        borderRadius: '3px 3px 0 0',
        backgroundColor: 'var(--green)'
    }
})

export const ListagemTab = styled(Tab)({
    minHeight: 42,
    padding: '10px 14px 12px',
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 650,
    color: '#4e5752',
    '&:hover': { color: 'var(--green-800)' },
    '&.Mui-selected': { color: 'var(--green-800)' }
})

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Mapa
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

export const MapToolbarGap = styled('div')({
    marginTop: 6
})

export const MapSlider = styled(Slider)({
    position: 'absolute',
    left: 16,
    right: 16,
    bottom: 10,
    width: 'auto',
    zIndex: 1100,
    color: 'var(--green-700)',
    '& .MuiSlider-rail': { backgroundColor: '#b8c0bb', opacity: 1 },
    '& .MuiSlider-thumb': {
        backgroundColor: '#ffffff',
        border: '2px solid var(--green-700)',
        boxShadow: '0 1px 3px rgba(0,0,0,.25)'
    }
})

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Carregamento
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

export const skeletonPulse = keyframes({
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.55 }
})

export const SkeletonBlock = styled('div')({
    backgroundColor: '#eef1ef',
    borderRadius: 9,
    animation: `${skeletonPulse} 1.4s ease-in-out infinite`
})

/** Barra fina de progresso indeterminado, para recarga sobre conteúdo já visível. */
export const WorkingBar = styled(LinearProgress)({
    flexShrink: 0,
    height: 3,
    backgroundColor: 'var(--green-100)',
    '& .MuiLinearProgress-bar': { backgroundColor: 'var(--green-700)' }
})
